import { useEffect, useRef } from 'react';

/**
 * A tap-to-place / drag-to-move location picker built on Leaflet + OpenStreetMap tiles.
 * Keyless (matches the app's existing Nominatim reverse-geocoding). Leaflet is loaded from the
 * CDN on first use so it adds no build dependency — the map needs the network for tiles anyway.
 *
 * The customer drops the pin by dragging it or tapping the map; the pin's lat/lng is reported
 * back via onChange. Passing new lat/lng in (e.g. after "Use my location") recenters the pin.
 */

// Jaipur city centre — a sensible default before the customer has a fix.
const DEFAULT: [number, number] = [26.9124, 75.7873];
const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

let leafletPromise: Promise<any> | null = null;
function loadLeaflet(): Promise<any> {
  const w = window as any;
  if (w.L) return Promise.resolve(w.L);
  if (leafletPromise) return leafletPromise;
  leafletPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve((window as any).L));
      existing.addEventListener('error', reject);
      if ((window as any).L) resolve((window as any).L);
      return;
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve((window as any).L);
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return leafletPromise;
}

// A themed teardrop pin (avoids Leaflet's default marker-image asset entirely).
function pinIcon(L: any) {
  return L.divIcon({
    className: '',
    html:
      '<svg width="30" height="40" viewBox="0 0 30 40" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 25 15 25s15-14.5 15-25C30 6.7 23.3 0 15 0z" fill="#e11d48"/>' +
      '<circle cx="15" cy="15" r="6" fill="#fff"/></svg>',
    iconSize: [30, 40],
    iconAnchor: [15, 40]
  });
}

interface MapPickerProps {
  lat: number | null;
  lng: number | null;
  onChange: (lat: number, lng: number) => void;
}

export default function MapPicker({ lat, lng, onChange }: MapPickerProps) {
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Create the map once Leaflet is available.
  useEffect(() => {
    let disposed = false;
    loadLeaflet()
      .then((L) => {
        if (disposed || !elRef.current || mapRef.current) return;
        const start: [number, number] = lat != null && lng != null ? [lat, lng] : DEFAULT;
        const map = L.map(elRef.current, { attributionControl: false, zoomControl: true }).setView(start, 16);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(map);
        const marker = L.marker(start, { draggable: true, icon: pinIcon(L) }).addTo(map);
        marker.on('dragend', () => {
          const p = marker.getLatLng();
          onChangeRef.current(p.lat, p.lng);
        });
        map.on('click', (e: any) => {
          marker.setLatLng(e.latlng);
          onChangeRef.current(e.latlng.lat, e.latlng.lng);
        });
        mapRef.current = map;
        markerRef.current = marker;
        // Leaflet needs a size recalc when it mounts inside a just-opened modal.
        setTimeout(() => map.invalidateSize(), 60);
      })
      .catch(() => {
        /* CDN blocked → the map simply doesn't render; manual entry + GPS still work. */
      });
    return () => {
      disposed = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter when the coordinates change from the outside (e.g. "Use my location").
  useEffect(() => {
    if (lat == null || lng == null || !mapRef.current || !markerRef.current) return;
    const cur = markerRef.current.getLatLng();
    if (Math.abs(cur.lat - lat) < 1e-6 && Math.abs(cur.lng - lng) < 1e-6) return; // no-op on our own updates
    markerRef.current.setLatLng([lat, lng]);
    mapRef.current.setView([lat, lng], Math.max(mapRef.current.getZoom(), 16));
  }, [lat, lng]);

  return (
    <div
      ref={elRef}
      className='mb-3 h-44 w-full overflow-hidden rounded-lg border border-border'
      role='application'
      aria-label='Delivery location map — drag the pin or tap to set your drop point'
    />
  );
}
